import { courseTabsClasses } from "@/classes/courseTabsClasses";
import { Button, ButtonProps } from "@/components/base/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/base/tooltip";
import { TransparentTextArea } from "@/components/input";
import { cn } from "@/lib/utils";
import useUiStore from "@/store/ui.state";
import { LangKey, useTranslation } from "@vixi/localization";
import _ from "lodash";
import { ComponentProps, forwardRef, useCallback, useMemo } from "react";
import { useCourseEditorContext } from "../context";
import CourseTabButton, { PseudoTabButton } from "./TabButton";
import { TabItemProps, TabItemRenameProps } from "./TabItemProps";

// Assets
import ArrowheadOutlineLeft from "@assets/icons/arrowhead-outline-left.svg?react";
import CaretDownIcon from "@assets/icons/caret-down.svg?react";
import CaretUpIcon from "@assets/icons/caret-up.svg?react";
import DeleteIcon from "@assets/icons/delete.svg?react";
import DuplicateIcon from "@assets/icons/duplicate.svg?react";
import EditIcon from "@assets/icons/pencil.svg?react";

// Constants
const iconStyle = cn("size-4.5");

const TabNumber = (props: ComponentProps<"span">) => (
  <span
    {...props}
    className={cn(
      "border-primary-light bg-paper text-primary-light absolute start-0 z-1 box-border min-w-7.5 rounded-[8px] border-2 px-1 py-0.5 text-center leading-[1.2]",
      "group-hover/tab:border-primary group-hover/tab:text-primary group-has-data-[state=active]/tab:border-primary group-has-data-[state=active]/tab:text-primary",
      props.className,
    )}
  />
);

interface TabArrowProps extends ButtonProps {
  left?: boolean;
  right?: boolean;
}

const TabArrow = forwardRef<HTMLButtonElement, TabArrowProps>((props, ref) => {
  const { left, right, className, ...rest } = props;
  const { isRtl } = useTranslation();

  const dir = (left && !isRtl) || (right && isRtl) ? "start" : "end";

  return (
    <Button
      ref={ref}
      size="icon"
      color="primary"
      variant="text"
      className={cn(
        courseTabsClasses.tabItemArrowDir(dir),
        "text-primary-light hover:primary-light absolute top-1/2 -translate-y-1/2",
        {
          "left-1": left,
          "right-1 -scale-x-100": right,
        },
        className,
      )}
      {...rest}
    />
  );
});

interface ModificationButtonProps extends ButtonProps {
  tooltip?: LangKey;
  hoverColor?: "default" | "delete";
}

const ModificationButton = forwardRef<HTMLButtonElement, ModificationButtonProps>((props, ref) => {
  const { t } = useTranslation();
  const { tooltip, hoverColor, ...rest } = props;

  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger>
        <Button
          ref={ref}
          variant="text"
          size="icon"
          color="muted"
          {...rest}
          className={cn(
            "text-gray p-1.5",
            {
              "hover:text-primary-light": hoverColor === "default",
              "hover:text-destructive": hoverColor === "delete",
            },
            props.className,
          )}
        />
      </TooltipTrigger>

      <TooltipContent size="text" variant="btnTip" side="top">
        {tooltip && <>{t(tooltip)}</>}
      </TooltipContent>
    </Tooltip>
  );
});

// const ModificationButton: FC<ComponentProps<typeof TabModificationButtonButton>> = (props) => {
//   const { t } = useTranslation();
//   const { tooltip, ...otherProps } = props;

//   return (
//     <TabModificationButtonTooltip title={(tooltip && t(tooltip)) || ""}>
//       <TabModificationButtonButton {...otherProps} />
//     </TabModificationButtonTooltip>
//   );
// };

type OutlinedTabItemProps = TabItemProps & TabItemRenameProps<HTMLTextAreaElement>;

/**
 * @summary This is a "tab item" for the `CourseEditor` component.
 * It is currently used to represent courses and lessons in the course editor.
 * @description It provides a tab item with an outline style, with a group of modification buttons allowing for renaming and deleting of the item the tab is representing.
 */
export const OutlinedTabItem = forwardRef<HTMLButtonElement, OutlinedTabItemProps>((props, ref) => {
  const {
    singleTitle,
    isRenaming,
    startRenaming,
    endRenaming,
    showNumber,
    renameInputRef,
    newName,
    setNewName,
    isSelected,
    selectedTab,
    setSelectedTab,
    path,
    text,
    onChange,
    tabNumber,
    disableDelete,
    ...otherProps
  } = props as OutlinedTabItemProps;

  const { t, tNum, isRtl } = useTranslation();
  const {
    showConfirmDialog,
    courseEditor: { mode },
  } = useUiStore();
  const isEditing = mode === "edit";

  const { courseData, setValue, deleteItem, duplicateItem, moveBy } = useCourseEditorContext();

  const handleDelete = useCallback(() => {
    deleteItem(path);

    // If the selected tab is deleted, update the selected tab index
    if (isSelected) {
      const newIndex = Math.max(selectedTab - 1, 1);
      setSelectedTab(newIndex);
    }

    return true;
  }, [deleteItem, isSelected, path, selectedTab, setSelectedTab]);

  // Show confirmation dialog for deletion
  const onDeleteClicked = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      if (e.shiftKey) {
        handleDelete();
        return;
      }

      showConfirmDialog({
        title: "Confirmation.DeleteTab.Title",
        description: (
          <p className="fs-12">
            {t("Confirmation.DeleteTab.Description")} "{text}"?
            <br />
            <em>{t("Confirmation.SkipByHoldingShift")}</em>
          </p>
        ),
        onConfirm: () => handleDelete(),
      });
    },
    [handleDelete],
  );

  const handleMove = useCallback(
    (n: number) => {
      const newIndex = moveBy(path, n);

      // Persist the currently selected tab
      if (isSelected) {
        // If the tab being moved is selected, update the selected tab index
        setSelectedTab(newIndex + 1);
      } else if (newIndex === selectedTab - 1) {
        // If the tab is being moved to the index of the selected tab, update the selected tab index
        setSelectedTab(selectedTab - n);
      }
    },
    [isSelected, moveBy, selectedTab, setSelectedTab, path],
  );

  const forwardedProps = {
    ...otherProps,
    showNumber,
  };

  // Check if there are multiple titles to enable navigation arrows
  const titles = useMemo(() => _.get(courseData, path.concat("titles")), [courseData, path]);
  const hasMultipleTitles = titles && Array.isArray(titles) && titles.length > 1;

  return (
    <>
      {isEditing && (
        <div
          className={cn(
            courseTabsClasses.modificationButtonsRoot,
            "pointer-events-none bottom-full -mt-7 flex w-full items-end justify-end opacity-0 [&_svg]:h-auto",
            "group-hover/tab:pointer-events-auto group-hover/tab:opacity-100 group-has-data-[state=active]/tab:pointer-events-auto group-has-data-[state=active]/tab:opacity-100",
          )}
        >
          <ModificationButton
            disabled={disableDelete}
            tooltip="Action.Delete"
            onClick={onDeleteClicked}
            hoverColor="delete"
          >
            <DeleteIcon className={iconStyle} />
          </ModificationButton>

          <ModificationButton tooltip="Action.Duplicate" onClick={() => duplicateItem(path)}>
            <DuplicateIcon className={iconStyle} />
          </ModificationButton>

          <ModificationButton tooltip="Action.MoveUp" onClick={() => handleMove(-1)}>
            <CaretUpIcon className={iconStyle} />
          </ModificationButton>

          <ModificationButton tooltip="Action.MoveDown" onClick={() => handleMove(1)}>
            <CaretDownIcon className={iconStyle} />
          </ModificationButton>

          <ModificationButton tooltip="Action.Rename" onClick={() => startRenaming()}>
            <EditIcon className={iconStyle} />
          </ModificationButton>
        </div>
      )}

      {showNumber && <TabNumber>{tNum(props.value)}</TabNumber>}

      <CourseTabButton
        ref={ref}
        {...forwardedProps}
        className={cn(isRenaming && "hidden", forwardedProps.className)}
        children={text}
      />

      {isRenaming && (
        <PseudoTabButton {...forwardedProps}>
          <TransparentTextArea
            ref={renameInputRef}
            className="fs-15 text-foreground block border-0 p-0 text-center"
            value={newName}
            onBlur={() => endRenaming()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") endRenaming({ singleTitle });
              else if (e.key === "Escape") endRenaming({ singleTitle, cancel: true });
            }}
            onChange={(e) => setNewName(e.target.value)}
          />
        </PseudoTabButton>
      )}

      {/* Prev Arrow - Always show and clickable, but only navigate if there are multiple titles */}
      <TabArrow
        onClick={() => {
          // Only navigate if there are multiple titles
          if (hasMultipleTitles) {
            setValue(path.concat("selectedTitle"), (prev: number) => Math.max(prev - 1, 0), {
              mustExist: false,
            });
          }
          // If no multiple titles, button is clickable but does nothing (for visual feedback)
        }}
        left={!isRtl}
        right={isRtl}
      >
        <ArrowheadOutlineLeft className={iconStyle} />
      </TabArrow>

      {/* Next Arrow - Always show and clickable, but only navigate if there are multiple titles */}
      <TabArrow
        onClick={() => {
          // Only navigate if there are multiple titles
          if (hasMultipleTitles && titles) {
            setValue(
              path.concat("selectedTitle"),
              (prev: number) => Math.min(prev + 1, (titles as string[]).length - 1),
              { mustExist: false },
            );
          }
          // If no multiple titles, button is clickable but does nothing (for visual feedback)
        }}
        left={isRtl}
        right={!isRtl}
      >
        <ArrowheadOutlineLeft className={iconStyle} />
      </TabArrow>
    </>
  );
});

export default OutlinedTabItem;
